import React from 'react';
import { t } from '@lingui/macro';
import styled from 'styled-components';

import { Box, Flex } from '@rover/kibble/core';
import MQ from '@rover/kibble/styles/MediaQuery';
import useTheme from '@rover/kibble/themes/useTheme';
import CatPlaceholder from '@rover/react-lib/src/images/placeholders/cat-placeholder.png';
import DogPlaceholder from '@rover/react-lib/src/images/placeholders/dog-placeholder.png';
import PawPlaceholder from '@rover/react-lib/src/images/placeholders/paw-placeholder.png';
import PersonPlaceholder from '@rover/react-lib/src/images/placeholders/person-placeholder.png';
import { buildSrcset } from '@rover/react-lib/src/utils/image';

export enum Size {
  ExtraSmall = 'ExtraSmall',
  Small = 'Small',
  Medium = 'Medium',
  Large = 'Large',
  Profile = 'Profile',
}

export enum AvatarType {
  Dog = 'dog',
  Cat = 'cat',
  Person = 'person',
  Other = 'other',
}

export type StyleProps = {
  size?: Size;
  showBorder?: boolean;
  diameter?: string | number;
  borderColor?: string;
  borderWidth?: string | number;
  borderRadius?: string | number;
  backgroundColor?: string;
  objectFit?: 'fill' | 'contain' | 'cover' | 'none' | 'scale-down';
};

export type Props = {
  imageUrl: string | null;
  iconOverlay?: string;
  avatarType?: AvatarType;
  alt?: string;
} & StyleProps;

export type AvatarSize = {
  diameter: number;
  borderRadius: number;
  borderWidth: number;
};

const EXTRA_SMALL_AVATAR_SIZE: AvatarSize = {
  diameter: 32,
  borderRadius: 8,
  borderWidth: 1,
};

const SMALL_AVATAR_SIZE: AvatarSize = {
  diameter: 48,
  borderRadius: 12,
  borderWidth: 1,
};

const MEDIUM_AVATAR_SIZE: AvatarSize = {
  diameter: 128,
  borderRadius: 16,
  borderWidth: 1,
};

const LARGE_AVATAR_SIZE: AvatarSize = {
  diameter: 225,
  borderRadius: 24,
  borderWidth: 1,
};

const PROFILE_AVATAR_SIZE: AvatarSize = {
  diameter: 112,
  borderRadius: 120,
  borderWidth: 1,
};

const DEFAULT_IMAGE_BY_TYPE = {
  cat: CatPlaceholder,
  dog: DogPlaceholder,
  person: PersonPlaceholder,
  other: PawPlaceholder,
};

const AVATAR_SIZE_MAP = new Map<Size, AvatarSize>([
  [Size.ExtraSmall, EXTRA_SMALL_AVATAR_SIZE],
  [Size.Small, SMALL_AVATAR_SIZE],
  [Size.Medium, MEDIUM_AVATAR_SIZE],
  [Size.Large, LARGE_AVATAR_SIZE],
  [Size.Profile, PROFILE_AVATAR_SIZE],
]);

export const getAvatarImage = (imageUrl: string | null, avatarType: AvatarType): string => {
  if (imageUrl) {
    return imageUrl;
  }
  return DEFAULT_IMAGE_BY_TYPE[avatarType] || DEFAULT_IMAGE_BY_TYPE.other;
};

export const getSize = (size: Size = Size.Medium): AvatarSize => {
  return AVATAR_SIZE_MAP.get(size) || MEDIUM_AVATAR_SIZE;
};

export const getAvatarSrcset = (
  imageUrl: string | null,
  avatarType: AvatarType,
  size?: number
): string => {
  if (imageUrl) {
    if (!size || imageUrl.startsWith('blob:') || imageUrl.startsWith('data:')) {
      // Skip srcset if size is not provided or cannot be applied cause image is a blob/data URI
      return imageUrl;
    }

    const url = imageUrl.split('?')[0];
    const sizesArray = [
      { width: `${size}`, height: `${size}` },
      { width: `${size * 2}`, height: `${size * 2}` },
    ];
    return buildSrcset(url, sizesArray)
      .map((u, i) => `${u} ${i + 1}x`)
      .join(', ');
  }
  return DEFAULT_IMAGE_BY_TYPE[avatarType] || DEFAULT_IMAGE_BY_TYPE.other;
};

const getDiameter = (
  diameterProp: string | number | undefined,
  defaultDiameter: number
): number => {
  if (diameterProp) {
    return typeof diameterProp === 'string'
      ? parseInt(diameterProp.split('px')[0], 10)
      : diameterProp;
  }
  return defaultDiameter;
};

export const StyledProfilePicture = styled.img`
  border-radius: 9999px;
`;

export const IconOverlay = styled.img`
  position: relative;
  top: 170px;
  right: 60px;
  width: 45px;
  height: 45px;
  ${MQ.MD_DOWN.toString()} {
    top: 90px;
    right: 25px;
    width: 32px;
    height: 32px;
  }
`;

const Avatar = (props: Props): JSX.Element => {
  const { colors } = useTheme();
  const {
    imageUrl,
    iconOverlay,
    size = Size.Medium,
    showBorder = true,
    diameter,
    borderColor = colors.neutral.white,
    backgroundColor,
    borderRadius,
    borderWidth,
    objectFit,
    avatarType = AvatarType.Other,
    alt,
  } = props;
  const avatarSize: AvatarSize = getSize(size);
  const sizeDiameter = getDiameter(diameter, avatarSize.diameter);
  const avatarSrcset = getAvatarSrcset(imageUrl, avatarType, sizeDiameter);
  const avatarImage = getAvatarImage(imageUrl, avatarType);

  const getAlt = (altProp: string | undefined, iconOverlayProp: string | undefined): string => {
    let altText = t`Profile image`;
    if (altProp) {
      altText = altProp;
    }
    if (iconOverlayProp) {
      altText += t` Verified background check.`;
    }
    return altText;
  };

  const altText = getAlt(alt, iconOverlay);
  return (
    <Flex>
      <Box
        testID="avatar-box"
        height={diameter}
        width={diameter}
        sx={{ backgroundColor, borderRadius }}
      >
        <StyledProfilePicture
          src={avatarImage}
          srcSet={avatarSrcset}
          alt={altText}
          style={{
            height: diameter ?? avatarSize.diameter,
            width: diameter ?? avatarSize.diameter,
            borderColor,
            borderRadius: borderRadius ?? avatarSize.borderRadius,
            borderWidth: showBorder ? borderWidth ?? avatarSize.borderWidth : 0,
            borderStyle: showBorder ? 'solid' : 'none',
            objectFit: objectFit || 'cover',
            ...(objectFit && { objectFit }),
          }}
        />
      </Box>
      {iconOverlay && <IconOverlay src={iconOverlay} alt="Icon overlay" />}
    </Flex>
  );
};

export default Avatar;
